import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Building2, Users, Activity, TrendingUp, Database, 
  ArrowUpRight, ArrowDownRight, CheckCircle, XCircle,
  AlertTriangle, Clock, Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import platformApi from '@/lib/platformApi';

export default function PlatformDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, hospitalsRes] = await Promise.all([
        platformApi.get('/platform/stats'),
        platformApi.get('/platform/hospitals?limit=5')
      ]);
      
      setStats(statsRes.data);
      setHospitals(hospitalsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load platform data');
      console.error('Platform data error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Hospitals',
      value: stats?.total_hospitals || 0,
      icon: Building2,
      color: 'from-blue-500 to-blue-600',
      change: stats?.growth_30d?.hospitals || 0,
      changeLabel: 'new this month'
    },
    {
      title: 'Total Patients',
      value: stats?.total_patients?.toLocaleString() || 0,
      icon: Users,
      color: 'from-emerald-500 to-teal-600',
      change: stats?.growth_30d?.patients || 0,
      changeLabel: 'new this month'
    },
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'from-purple-500 to-purple-600',
      change: stats?.growth_30d?.users || 0,
      changeLabel: 'new this month'
    },
    {
      title: 'Total Visits',
      value: stats?.total_visits?.toLocaleString() || 0,
      icon: Activity,
      color: 'from-amber-500 to-orange-600',
      change: '+12%',
      changeLabel: 'vs last month'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Platform Overview</h1>
          <p className="text-slate-400">Monitor your multi-tenant healthcare platform</p>
        </div>
        <Button 
          onClick={() => navigate('/platform-admin/hospitals')}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="add-hospital-btn"
        >
          <Building2 className="mr-2 h-4 w-4" />
          Add Hospital
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-slate-400">{stat.title}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs">
                <ArrowUpRight className="h-3 w-3 text-emerald-400" />
                <span className="text-emerald-400 font-medium">{stat.change}</span>
                <span className="text-slate-500">{stat.changeLabel}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subscription Tiers */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-400" />
              Subscription Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {['enterprise', 'professional', 'basic'].map((tier) => {
              const count = stats?.tier_breakdown?.[tier] || 0;
              const total = stats?.total_hospitals || 1;
              const percentage = Math.round((count / total) * 100);
              const colors = {
                enterprise: 'bg-gradient-to-r from-amber-500 to-orange-500',
                professional: 'bg-gradient-to-r from-purple-500 to-purple-600',
                basic: 'bg-gradient-to-r from-slate-500 to-slate-600'
              };
              
              return (
                <div key={tier}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300 capitalize">{tier}</span>
                    <span className="text-white font-medium">{count} hospitals</span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${colors[tier]} rounded-full transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Status Overview */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Activity className="h-5 w-5 text-emerald-400" />
              Hospital Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {[
                { status: 'active', label: 'Active', icon: CheckCircle, color: 'text-emerald-400' },
                { status: 'suspended', label: 'Suspended', icon: XCircle, color: 'text-red-400' },
                { status: 'trial', label: 'Trial', icon: Clock, color: 'text-amber-400' },
                { status: 'expired', label: 'Expired', icon: AlertTriangle, color: 'text-slate-400' }
              ].map((item) => (
                <div key={item.status} className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                  <item.icon className={`h-5 w-5 ${item.color}`} />
                  <div>
                    <p className="text-lg font-bold text-white">
                      {stats?.status_breakdown?.[item.status] || 0}
                    </p>
                    <p className="text-xs text-slate-400">{item.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Hospitals */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-400" />
            Recent Hospitals
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/platform-admin/hospitals')}
            className="text-emerald-400 hover:text-emerald-300 hover:bg-slate-700"
          >
            View All
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                  <th className="pb-3 font-medium">Hospital</th>
                  <th className="pb-3 font-medium">Tier</th>
                  <th className="pb-3 font-medium">Patients</th>
                  <th className="pb-3 font-medium">Users</th>
                  <th className="pb-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {hospitals.map((hospital) => (
                  <tr key={hospital.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className="py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                          {hospital.name?.charAt(0) || 'H'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{hospital.name}</p>
                          <p className="text-xs text-slate-500">{hospital.city || 'N/A'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                        hospital.subscription_tier === 'enterprise' ? 'bg-amber-500/20 text-amber-400' :
                        hospital.subscription_tier === 'professional' ? 'bg-purple-500/20 text-purple-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {hospital.subscription_tier}
                      </span>
                    </td>
                    <td className="py-3 text-slate-300">{hospital.patient_count || 0}</td>
                    <td className="py-3 text-slate-300">{hospital.user_count || 0}</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                        hospital.subscription_status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                        hospital.subscription_status === 'suspended' ? 'bg-red-500/20 text-red-400' :
                        hospital.subscription_status === 'trial' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {hospital.subscription_status}
                      </span>
                    </td>
                  </tr>
                ))}
                {hospitals.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-slate-500">
                      No hospitals found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
